import { mailTypeLabel } from "@shared/mail-rules.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Card,
  FIELD,
  FILL_BODY,
  FILL_CARD,
  FILL_PAGE,
  OUTLINE_BTN,
  PageHeader,
  Pagination,
  PRIMARY_BTN,
  ROW_SCROLL,
  STICKY_THEAD,
  usePageSize,
} from "../components/account-ui.tsx";
import { Modal, useToast } from "../components/legacy/ui.tsx";
import { EditIcon, PlusIcon, TrashcanIcon } from "../icons/index.tsx";
import { type AppConfig, api, type PriceTierRow, type Product } from "../lib/api.ts";
import { dollarsToCents, formatUnitDollars, tierRangeEnd } from "../lib/catalog.ts";
import { formatPrices, isStaffRole } from "../lib/legacy.ts";

export const Route = createFileRoute("/app/$customerId/products")({
  component: ManageProducts,
  beforeLoad: async ({ context, params }) => {
    const config = await context.queryClient.ensureQueryData({
      queryKey: ["config"],
      queryFn: () => api.get<AppConfig>("/config"),
    });
    if (!config.user || !isStaffRole(config.user.role)) {
      throw redirect({
        to: "/app/$customerId/mycampaigns",
        params: { customerId: params.customerId },
      });
    }
  },
});

interface Tier {
  minQty: number;
  /** Dollar text as typed. Converted to cents only when saving. */
  price: string;
}

/** Seeded trim sizes. A-series are portrait; DL and MaxPop are the landscape pieces. */
const PAPER_MM: Record<string, { widthMm: number; heightMm: number }> = {
  DL: { widthMm: 210, heightMm: 99 },
  MaxPop: { widthMm: 240, heightMm: 130 },
  A4: { widthMm: 210, heightMm: 297 },
  A5: { widthMm: 148, heightMm: 210 },
  A6: { widthMm: 105, heightMm: 148 },
};

function pricingMessage(tiers: Tier[]): string | null {
  if (tiers.length === 0 || Math.min(...tiers.map((t) => t.minQty)) !== 1) {
    return "The first quantity break must start at 1.";
  }
  if (new Set(tiers.map((t) => t.minQty)).size !== tiers.length) {
    return "Two breaks share the same quantity.";
  }
  const missingPrice = tiers.some((t) => {
    const n = Number(t.price);
    return t.price.trim() === "" || !Number.isFinite(n) || n < 0;
  });
  if (missingPrice) return "Enter a price for each break.";
  return null;
}

interface Form {
  name: string;
  slug: string;
  description: string;
  size: string;
  pages: number;
  widthMm: number;
  heightMm: number;
  gsm: string;
  stock: string;
  addressedMail: boolean;
  unaddressedMail: boolean;
  image: string;
  sortOrder: number;
  active: boolean;
  tiers: Tier[];
}

const LEGACY_TIERS: Tier[] = [
  { minQty: 1, price: formatUnitDollars(110) },
  { minQty: 500, price: formatUnitDollars(110) },
  { minQty: 1000, price: formatUnitDollars(25.5) },
  { minQty: 2000, price: formatUnitDollars(20) },
  { minQty: 3000, price: formatUnitDollars(15) },
];

const EMPTY: Form = {
  name: "",
  slug: "",
  description: "",
  size: "DL",
  pages: 2,
  widthMm: 210,
  heightMm: 99,
  gsm: "",
  stock: "",
  addressedMail: false,
  unaddressedMail: true,
  image: "",
  sortOrder: 0,
  active: true,
  tiers: LEGACY_TIERS,
};

/** Fill millimetres only while they still match a standard size or a new product. */
function withSize(form: Form, size: string): Form {
  const next = PAPER_MM[size];
  const prev = PAPER_MM[form.size];
  const stillStandard =
    (prev !== undefined && form.widthMm === prev.widthMm && form.heightMm === prev.heightMm) ||
    (form.widthMm === EMPTY.widthMm && form.heightMm === EMPTY.heightMm);
  if (!next || !stillStandard) return { ...form, size };
  return { ...form, size, widthMm: next.widthMm, heightMm: next.heightMm };
}

/**
 * Product management, standing in for the Vendure admin: the catalogue
 * table with the Vendure custom fields (dimensions, quantity breaks) and
 * the extra attributes the campaign UI shows (size label, pages, mail type).
 */
function ManageProducts() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [dropImage, setDropImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const [perPage, setPerPage] = usePageSize();
  const listKey = String(perPage);
  const [paged, setPaged] = useState({ key: listKey, n: 0 });

  const { data } = useQuery({
    queryKey: ["catalog", "all"],
    queryFn: () =>
      api.get<{ products: Product[]; tiers: PriceTierRow[] }>("/catalog/products?all=1"),
  });

  useEffect(() => {
    if (editing === null) return;
    if (editing === "new") {
      setForm(EMPTY);
      return;
    }
    const tiers = (data?.tiers ?? [])
      .filter((t) => t.product_id === editing.id)
      .sort((a, b) => a.min_qty - b.min_qty)
      .map((t) => ({ minQty: t.min_qty, price: formatUnitDollars(t.unit_cents) }));
    setForm({
      name: editing.name,
      slug: editing.slug,
      description: editing.description ?? "",
      size: editing.size ?? "",
      pages: editing.pages,
      widthMm: editing.width_mm,
      heightMm: editing.height_mm,
      gsm: editing.gsm?.toString() ?? "",
      stock: editing.stock ?? "",
      addressedMail: editing.addressed_mail === 1,
      unaddressedMail: editing.unaddressed_mail === 1,
      image: editing.image ?? "",
      sortOrder: editing.sort_order,
      active: editing.active !== 0,
      tiers: tiers.length ? tiers : LEGACY_TIERS,
    });
    setPendingFile(null);
    setDropImage(false);
  }, [editing, data]);

  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["catalog"] });

  const uploadImage = (id: string) => {
    if (!pendingFile) return Promise.resolve();
    const body = new FormData();
    body.set("file", pendingFile);
    return api.upload(`/catalog/products/${id}/image`, body);
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name,
        slug: form.slug || undefined,
        description: form.description || null,
        size: form.size || null,
        pages: Number(form.pages),
        widthMm: Number(form.widthMm),
        heightMm: Number(form.heightMm),
        gsm: form.gsm ? Number(form.gsm) : null,
        stock: form.stock || null,
        addressedMail: form.addressedMail,
        unaddressedMail: form.unaddressedMail,
        sortOrder: Number(form.sortOrder),
        active: form.active,
        tiers: form.tiers.map((t) => ({
          minQty: Number(t.minQty),
          unitCents: dollarsToCents(Number(t.price)),
        })),
      };
      if (editing === null) return;
      if (editing === "new") {
        const created = await api.post<{ product: Product }>("/catalog/products", body);
        await uploadImage(created.product.id);
        return;
      }
      const id = editing.id;
      if (pendingFile) await uploadImage(id);
      else if (dropImage) await api.delete(`/catalog/products/${id}/image`);
      await api.patch(`/catalog/products/${id}`, body);
    },
    onSuccess: () => {
      invalidate();
      setEditing(null);
      showToast("Product saved", "success");
    },
    onError: (e: Error) => showToast(e.message, "error"),
  });

  const retire = useMutation({
    mutationFn: (id: string) => api.delete(`/catalog/products/${id}`),
    onSuccess: () => {
      invalidate();
      showToast("Product retired", "success");
    },
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => api.patch(`/catalog/products/${id}`, { active: true }),
    onSuccess: () => invalidate(),
  });

  const tiersFor = (id: string) =>
    (data?.tiers ?? []).filter((t) => t.product_id === id).sort((a, b) => a.min_qty - b.min_qty);
  const setTier = (i: number, patch: Partial<Tier>) =>
    setForm({ ...form, tiers: form.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const priceError = pricingMessage(form.tiers);
  const quantities = form.tiers.map((t) => t.minQty);
  const needsMailType = !form.addressedMail && !form.unaddressedMail;
  const imageSrc = previewUrl || (dropImage ? "" : form.image);

  const products = data?.products ?? [];
  const page = Math.min(
    paged.key === listKey ? paged.n : 0,
    Math.max(0, Math.ceil(products.length / perPage) - 1),
  );
  const shownProducts = products.slice(page * perPage, (page + 1) * perPage);

  return (
    <div className={FILL_PAGE}>
      <PageHeader
        title="Products"
        subtitle="What customers can start a campaign from."
        action={
          <button type="button" className={PRIMARY_BTN} onClick={() => setEditing("new")}>
            <PlusIcon className="h-5 w-5" /> New Product
          </button>
        }
      />
      <Card className={FILL_CARD} bodyClass={FILL_BODY}>
        <div className={ROW_SCROLL}>
          <table className="w-full text-left text-sm">
            <thead className={STICKY_THEAD}>
              <tr>
                <th className="px-5 py-3 font-semibold">Product</th>
                <th className="px-5 py-3 font-semibold">Size</th>
                <th className="px-5 py-3 font-semibold">Mail</th>
                <th className="hidden px-5 py-3 font-semibold lg:table-cell">Quantity breaks</th>
                <th className="px-5 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shownProducts.map((p) => (
                <tr key={p.id} className={p.active === 0 ? "opacity-50" : ""}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-1">
                        {p.image && (
                          <img
                            src={p.image}
                            alt=""
                            className="max-h-full max-w-full object-contain"
                          />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">
                          {p.name}
                          {p.active === 0 && (
                            <span className="ml-2 inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                              Retired
                            </span>
                          )}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {p.slug}
                          {p.description ? ` · ${p.description}` : ""}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-gray-600">
                    <span className="font-medium text-gray-900">{p.size}</span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      {p.width_mm} x {p.height_mm}mm · {p.pages}pp
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                        p.addressed_mail === 1 && p.unaddressed_mail === 1
                          ? "bg-gray-100 text-gray-700"
                          : p.addressed_mail
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-sky-100 text-sky-800"
                      }`}
                    >
                      {mailTypeLabel(p)}
                    </span>
                  </td>
                  <td className="hidden whitespace-nowrap px-5 py-4 text-xs tabular-nums text-gray-600 lg:table-cell">
                    {tiersFor(p.id).map((t) => (
                      <div key={t.min_qty}>
                        {t.min_qty}+ · ${formatPrices(t.unit_cents / 100)}
                      </div>
                    ))}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50"
                        onClick={() => setEditing(p)}
                      >
                        <EditIcon className="h-4 w-4" /> Edit
                      </button>
                      {p.active === 0 ? (
                        <button
                          type="button"
                          className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                          onClick={() => reactivate.mutate(p.id)}
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          title="Retire product"
                          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600"
                          onClick={() => retire.mutate(p.id)}
                        >
                          <TrashcanIcon width={14} height={14} className="fill-current" /> Retire
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={perPage}
          total={products.length}
          onPage={(n) => setPaged({ key: listKey, n })}
          onPageSize={setPerPage}
        />
      </Card>

      <Modal
        open={editing !== null}
        title={
          editing === "new" ? "New Product" : `Edit ${(editing as Product | null)?.name ?? ""}`
        }
        submitText="Save"
        disableSubmitButton={!form.name || save.isPending || needsMailType || priceError !== null}
        onSubmit={() => save.mutate()}
        onCancel={() => setEditing(null)}
        icon={<EditIcon />}
      >
        <div className="space-y-5 text-sm">
          <section>
            <h4 className="text-sm font-semibold text-gray-900">Details</h4>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-gray-700 sm:col-span-2">
                Name
                <input
                  className={`${FIELD} mt-1`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700 sm:col-span-2">
                Description
                <input
                  className={`${FIELD} mt-1`}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Slug
                <input
                  className={`${FIELD} mt-1`}
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">
                  Leave blank to build it from the name.
                </span>
              </label>
              <div>
                <span className="block text-sm font-medium text-gray-700">Image</span>
                <div className="mt-1 flex items-center gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {imageSrc && (
                      <img
                        key={imageSrc}
                        src={imageSrc}
                        alt=""
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                  </span>
                  <span className="flex gap-2">
                    <button
                      type="button"
                      className={OUTLINE_BTN}
                      onClick={() => imageInput.current?.click()}
                    >
                      Upload
                    </button>
                    {imageSrc && (
                      <button
                        type="button"
                        className={OUTLINE_BTN}
                        onClick={() => {
                          setPendingFile(null);
                          setDropImage(true);
                          if (imageInput.current) imageInput.current.value = "";
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </span>
                </div>
                <input
                  ref={imageInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/heic,.heic"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setPendingFile(file);
                    setDropImage(false);
                  }}
                />
              </div>
            </div>
          </section>

          <section className="border-t border-gray-100 pt-5">
            <h4 className="text-sm font-semibold text-gray-900">Print</h4>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-gray-700">
                Size label
                <select
                  className={`${FIELD} mt-1`}
                  value={form.size}
                  onChange={(e) => setForm(withSize(form, e.target.value))}
                >
                  <option value="DL">DL</option>
                  <option value="MaxPop">MaxPop</option>
                  <option value="A4">A4</option>
                  <option value="A5">A5</option>
                  <option value="A6">A6</option>
                  <option value="">Other</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Pages
                <select
                  className={`${FIELD} mt-1`}
                  value={form.pages}
                  onChange={(e) => setForm({ ...form, pages: Number(e.target.value) })}
                >
                  <option value={2}>2 pages</option>
                  <option value={4}>4 pages</option>
                  <option value={6}>6 pages</option>
                  <option value={8}>8 pages</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Width (mm)
                <input
                  className={`${FIELD} mt-1`}
                  type="number"
                  value={form.widthMm}
                  onChange={(e) => setForm({ ...form, widthMm: Number(e.target.value) })}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Height (mm)
                <input
                  className={`${FIELD} mt-1`}
                  type="number"
                  value={form.heightMm}
                  onChange={(e) => setForm({ ...form, heightMm: Number(e.target.value) })}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                GSM
                <input
                  className={`${FIELD} mt-1`}
                  type="number"
                  value={form.gsm}
                  onChange={(e) => setForm({ ...form, gsm: e.target.value })}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Stock
                <input
                  className={`${FIELD} mt-1`}
                  value={form.stock}
                  placeholder="gloss art"
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                />
              </label>
            </div>
          </section>

          <section className="border-t border-gray-100 pt-5">
            <h4 className="text-sm font-semibold text-gray-900">Catalogue</h4>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-gray-700">
                Sort order
                <input
                  className={`${FIELD} mt-1`}
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">
                  Lower numbers appear first.
                </span>
              </label>
              <div className="flex flex-col gap-2 sm:pt-7">
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={form.addressedMail}
                      onChange={(e) => setForm({ ...form, addressedMail: e.target.checked })}
                    />
                    Addressed
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={form.unaddressedMail}
                      onChange={(e) => setForm({ ...form, unaddressedMail: e.target.checked })}
                    />
                    Unaddressed
                  </label>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                  Active
                  <span className="font-normal text-gray-500">Visible to customers</span>
                </label>
                {needsMailType && (
                  <p className="text-xs text-red-600">Choose at least one mail type.</p>
                )}
              </div>
            </div>
          </section>

          <section className="border-t border-gray-100 pt-5">
            <h4 className="text-sm font-semibold text-gray-900">Pricing</h4>
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="pb-2 pr-3 font-semibold">From quantity</th>
                  <th className="pb-2 pr-3 font-semibold">Up to</th>
                  <th className="pb-2 pr-3 font-semibold">Unit price</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {form.tiers.map((t, i) => (
                  <tr key={i}>
                    <td className="py-1 pr-3">
                      <input
                        className={FIELD}
                        type="number"
                        min={1}
                        value={t.minQty}
                        onChange={(e) => setTier(i, { minQty: Number(e.target.value) })}
                      />
                    </td>
                    <td className="py-1 pr-3 tabular-nums text-gray-500">
                      {tierRangeEnd(t.minQty, quantities)}
                    </td>
                    <td className="py-1 pr-3">
                      <div className="relative">
                        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500">
                          $
                        </span>
                        <input
                          className={`${FIELD} pl-7`}
                          inputMode="decimal"
                          value={t.price}
                          onChange={(e) => setTier(i, { price: e.target.value })}
                          onBlur={(e) => {
                            const raw = e.currentTarget.value;
                            const n = Number(raw);
                            if (raw.trim() === "" || !Number.isFinite(n) || n < 0) return;
                            setTier(i, { price: formatUnitDollars(dollarsToCents(n)) });
                          }}
                        />
                      </div>
                    </td>
                    <td className="py-1">
                      <button
                        type="button"
                        aria-label="Remove break"
                        className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        disabled={form.tiers.length <= 1}
                        onClick={() =>
                          setForm({ ...form, tiers: form.tiers.filter((_, j) => j !== i) })
                        }
                      >
                        <TrashcanIcon width={14} height={14} className="fill-current" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() =>
                setForm({
                  ...form,
                  tiers: [
                    ...form.tiers,
                    {
                      minQty: (form.tiers[form.tiers.length - 1]?.minQty ?? 0) + 1000,
                      price: formatUnitDollars(10),
                    },
                  ],
                })
              }
            >
              <PlusIcon /> Add Break
            </button>
            {priceError && <p className="mt-2 text-xs text-red-600">{priceError}</p>}
          </section>
        </div>
      </Modal>
    </div>
  );
}
